import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';

export type SupplierSort = 'default' | 'total' | 'bestPrices';

/** Colunas fixas do mapa que o usuário pode compactar para só o ícone. */
export type CollapsibleMapColumn = 'erp' | 'branch';

/** Preferências que valem para todos os mapas (não dependem da solicitação). */
export interface QuotationMapGlobalPreferences {
  collapsedColumns?: CollapsibleMapColumn[];
}

/** Preferências de tela do mapa de cotação, guardadas por solicitação no navegador. */
export interface QuotationMapPreferences {
  hiddenSupplierIds?: string[];
  tabIndex?: number;
  supplierSort?: SupplierSort;
}

/**
 * Persiste em `localStorage` o que é só conveniência de tela (colunas recolhidas, aba ativa,
 * ordenação). Nada aqui afeta seleção, totais ou rascunho, que ficam no backend.
 * O app é SSR: no servidor não há `localStorage`, e leitura/escrita são best-effort.
 */
@Injectable({ providedIn: 'root' })
export class QuotationMapPreferencesService {
  private static readonly PREFIX = 'ebarn_quotation_map_prefs:';
  private static readonly GLOBAL_KEY = 'ebarn_quotation_map_prefs_global';
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  get(purchaseId: string | undefined | null): QuotationMapPreferences {
    if (!this.isBrowser || !purchaseId) return {};
    try {
      const raw = localStorage.getItem(QuotationMapPreferencesService.PREFIX + purchaseId);
      return raw ? (JSON.parse(raw) as QuotationMapPreferences) : {};
    } catch {
      return {};
    }
  }

  patch(purchaseId: string | undefined | null, partial: QuotationMapPreferences): void {
    if (!this.isBrowser || !purchaseId) return;
    try {
      const next = { ...this.get(purchaseId), ...partial };
      localStorage.setItem(QuotationMapPreferencesService.PREFIX + purchaseId, JSON.stringify(next));
    } catch {
      /* armazenamento indisponível (modo privado, cota cheia): segue sem persistir */
    }
  }

  getGlobal(): QuotationMapGlobalPreferences {
    if (!this.isBrowser) return {};
    try {
      const raw = localStorage.getItem(QuotationMapPreferencesService.GLOBAL_KEY);
      return raw ? (JSON.parse(raw) as QuotationMapGlobalPreferences) : {};
    } catch {
      return {};
    }
  }

  patchGlobal(partial: QuotationMapGlobalPreferences): void {
    if (!this.isBrowser) return;
    try {
      localStorage.setItem(QuotationMapPreferencesService.GLOBAL_KEY, JSON.stringify({ ...this.getGlobal(), ...partial }));
    } catch {
      /* armazenamento indisponível: segue sem persistir */
    }
  }
}
